import { styled } from "styled-components";

import {
  AnchorLinkButton,
  colors,
  desktop,
  Modal,
  tablet,
  Text,
} from "@vericus/cadmus-ui";

import {
  DuplicatePastAssessmentCallback,
  IPastAssessment,
  PreviewPastAssessmentCallback,
} from "@/data/templates";
import { SubjectFragment, SubjectGroup } from "@/generated/graphql";

import { PastAssessments } from "./past-assessments";
import { PastAssessmentsFilters } from "./past-assessments-filters";
import { PastAssessmentSkeletons } from "./past-assessments-skeleton";

export interface PastAssessmentsRootProps {
  loading: boolean;
  pastAssessments: IPastAssessment[];
  /**
   * Whether the exams feature is enabled.
   */
  areExamsEnabled: boolean;
  /**
   * Whether the destination assessment is a group assessment. Drives which
   * type/format dropdown is rendered in the filters.
   */
  isGroupAssessment: boolean;
  /**
   * Whether the destination task is multi-format. When true, sources are
   * constrained to multi-format and the Format dropdown is hidden.
   */
  isMultiformat: boolean;
  onDuplicate: DuplicatePastAssessmentCallback;
  onPreview: PreviewPastAssessmentCallback;
}

export const PastAssessmentsRoot = (props: PastAssessmentsRootProps) => {
  const groupedSubjects = new Map<string, SubjectFragment>();
  const subjects = props.pastAssessments.flatMap((pastAssessment) => {
    if (groupedSubjects.has(pastAssessment.subject.id)) {
      return [];
    }

    groupedSubjects.set(pastAssessment.subject.id, pastAssessment.subject);
    return [pastAssessment.subject];
  });

  // Distinct set of subject groups across the listed assessments' subjects. A
  // subject can belong to several groups; each contributes to the filter list.
  const seenSubjectGroups = new Map<string, Pick<SubjectGroup, "id" | "name">>();
  const subjectGroups = props.pastAssessments.flatMap((pastAssessment) =>
    pastAssessment.subject.subjectGroups.flatMap((subjectGroup) => {
      if (seenSubjectGroups.has(subjectGroup.id)) {
        return [];
      }

      seenSubjectGroups.set(subjectGroup.id, subjectGroup);
      return [subjectGroup];
    })
  );

  return (
    <Container>
      <Header>
        <Modal.Title asChild>
          <Text kind={"headingTwo"} textAlign={"center"}>
            Past assessments
          </Text>
        </Modal.Title>

        <Modal.Description asChild>
          <PastAssessmentText kind="bodyMd" as="div">
            <Text kind={"bodyMd"} textAlign={"center"}>
              Save time by using existing instructions and settings. Make
              updates along the way.
            </Text>
            <AnchorLinkButton
              kind="primary"
              external
              href="https://support.cadmus.io/reusing-past-assessments"
            >
              Learn more
            </AnchorLinkButton>
          </PastAssessmentText>
        </Modal.Description>
      </Header>
      {!props.loading && props.pastAssessments?.length > 0 && (
        <FiltersContainer>
          <PastAssessmentsFilters
            areExamsEnabled={props.areExamsEnabled}
            subjects={subjects}
            subjectGroups={subjectGroups}
            isGroupAssessment={props.isGroupAssessment}
            isMultiformat={props.isMultiformat}
          />
        </FiltersContainer>
      )}
      {props.loading ? (
        <PastAssessmentSkeletons count={6} />
      ) : (
        <PastAssessments
          pastAssessments={props.pastAssessments}
          isMultiformat={props.isMultiformat}
          isGroupAssessment={props.isGroupAssessment}
          onDuplicate={props.onDuplicate}
          onPreview={props.onPreview}
        />
      )}
    </Container>
  );
};

const PastAssessmentText = styled(Text)`
  text-align: center;
`;

// color: ${colors.black100};
const FiltersContainer = styled.div`
  position: sticky;
  top: 0;
  z-index: 2;
  background-color: ${colors.white100};
  padding-bottom: 16px;
`;

const Header = styled.div``;

const Container = styled.div<{ px?: number; py?: number }>`
  padding: ${(p) => p.py ?? 64}px ${(p) => p.px ?? 64}px;
  // otherwise, the hover control bar overflows.
  min-width: 260px;
  padding: 48px 20px;
  ${tablet`
    padding: 48px 21px;
  `}
  ${desktop`
    padding: 64px 64px;
  `}
`;
